const DATE_ONLY_PATTERN = /^\d{4}-\d{2}-\d{2}$/
const FALLBACK_TIMEZONE = 'UTC'
const DATE_TIME_DISPLAY_OPTIONS: Intl.DateTimeFormatOptions = {
  year: 'numeric',
  month: '2-digit',
  day: '2-digit',
  hour: '2-digit',
  minute: '2-digit',
}
type UtcTimeParts = {
  hours: number
  minutes: number
  seconds: number
  milliseconds: number
}

const START_OF_DAY_UTC: UtcTimeParts = { hours: 0, minutes: 0, seconds: 0, milliseconds: 0 }
const END_OF_DAY_UTC: UtcTimeParts = { hours: 23, minutes: 59, seconds: 59, milliseconds: 999 }

export type DashboardTimeGranularity =
  | 'year'
  | 'quarter'
  | 'month'
  | 'week'
  | 'day'
  | 'quarter_day'
  | 'hour'
  | 'event'

// Shallowest to deepest. Mirrors the union above, which carries no runtime order.
const GRANULARITY_ORDER: DashboardTimeGranularity[] = [
  'year',
  'quarter',
  'month',
  'week',
  'day',
  'quarter_day',
  'hour',
  'event',
]

/**
 * Caps a granularity at the deepest bucket the active series supports.
 *
 * A date-of-service series has no bucket below the day: the stored value
 * carries the offset of the place of service, and is fabricated at local
 * midnight whenever the EHR page exposed no time.
 */
export const clampToFloor = (
  granularity: DashboardTimeGranularity,
  floor: DashboardTimeGranularity
): DashboardTimeGranularity =>
  GRANULARITY_ORDER.indexOf(granularity) > GRANULARITY_ORDER.indexOf(floor)
    ? floor
    : granularity

/**
 * Moves one step deeper in the drilldown hierarchy.
 */
export const getNextGranularity = (
  granularity: DashboardTimeGranularity
): DashboardTimeGranularity => {
  const currentIndex = GRANULARITY_ORDER.indexOf(granularity)
  if (currentIndex < 0 || currentIndex === GRANULARITY_ORDER.length - 1) return 'event'
  return GRANULARITY_ORDER[currentIndex + 1]
}

const normalizeInput = (value?: string | null) => {
  if (value === null || value === undefined) return undefined
  const trimmed = value.trim()
  return trimmed.length > 0 ? trimmed : undefined
}

/**
 * Safely parses an ISO-like timestamp string into a Date.
 */
export const parseIsoToDate = (value?: string | null): Date | undefined => {
  const normalized = normalizeInput(value)
  if (!normalized) return undefined
  const parsedDate = new Date(normalized)
  return Number.isFinite(parsedDate.getTime()) ? parsedDate : undefined
}

/**
 * Safely parses an ISO-like timestamp string into epoch milliseconds.
 */
export const parseIsoToMs = (value?: string | null): number | undefined => {
  const parsedDate = parseIsoToDate(value)
  return parsedDate ? parsedDate.getTime() : undefined
}

/**
 * Formats an ISO-like timestamp into the user's local date representation.
 */
export const formatLocalDateOnly = (value: string): string => {
  const date = parseIsoToDate(value)
  return date ? date.toLocaleDateString() : ''
}

/**
 * Formats an ISO-like timestamp into local date+time in US style.
 */
export const formatLocalDateTime = (value: string): string => {
  const date = parseIsoToDate(value)
  return date ? date.toLocaleString('en-US', DATE_TIME_DISPLAY_OPTIONS) : ''
}

const VISIT_DATE_PATTERN = /^(\d{4})-(\d{2})-(\d{2})(?:T(\d{2}):(\d{2}))?/

/**
 * Formats a stored date of service for display.
 *
 * The value carries the offset of the place of service, so the calendar day and
 * wall clock are read straight out of the string. Passing it through `new Date()`
 * would re-express the instant in the viewer's timezone and can shift the day,
 * which is the mismatch this feature exists to remove.
 *
 * A time of exactly midnight is dropped: the extension anchors a date-only EHR
 * field to local midnight, so a rendered "12:00 AM" would be fabricated.
 */
export const formatVisitDate = (value?: string | null): string => {
  const match = value ? VISIT_DATE_PATTERN.exec(value.trim()) : null
  if (!match) return ''

  const [, year, month, day, hours, minutes] = match
  const calendarDay = `${Number(month)}/${Number(day)}/${year}`
  if (hours === undefined || minutes === undefined) return calendarDay

  const hour24 = Number(hours)
  if (hour24 === 0 && Number(minutes) === 0) return calendarDay

  const meridiem = hour24 < 12 ? 'AM' : 'PM'
  return `${calendarDay} ${hour24 % 12 || 12}:${minutes} ${meridiem}`
}

const formatDateWithParts = (year: number, monthIndex: number, dayOfMonth: number) => {
  const month = String(monthIndex + 1).padStart(2, '0')
  const day = String(dayOfMonth).padStart(2, '0')
  return `${year}-${month}-${day}`
}

/**
 * Formats a Date as YYYY-MM-DD in local time.
 */
export const formatLocalDate = (date: Date) =>
  formatDateWithParts(date.getFullYear(), date.getMonth(), date.getDate())

/**
 * Formats a Date as YYYY-MM-DD in UTC.
 */
export const formatUtcDate = (date: Date) =>
  formatDateWithParts(date.getUTCFullYear(), date.getUTCMonth(), date.getUTCDate())

/**
 * Resolves the user's IANA timezone for dashboard API requests.
 * Falls back to UTC if the runtime cannot provide one.
 */
export const getDashboardTimezone = (): string =>
  Intl.DateTimeFormat().resolvedOptions().timeZone || FALLBACK_TIMEZONE

/** Extracts YYYY-MM-DD + hour in the provided timezone from epoch milliseconds. */
export const getDateHourInTimezone = (timestampMs: number, timezone: string) => {
  const date = new Date(timestampMs)
  if (Number.isNaN(date.getTime())) return { date: undefined, hour: undefined }

  const parts = new Intl.DateTimeFormat('en-US', {
    timeZone: timezone,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    hourCycle: 'h23',
  }).formatToParts(date)
  const getPart = (type: string) => parts.find((part) => part.type === type)?.value
  const year = getPart('year')
  const month = getPart('month')
  const day = getPart('day')
  const hour = Number.parseInt(getPart('hour') ?? '', 10)
  if (!year || !month || !day || !Number.isFinite(hour)) return { date: undefined, hour: undefined }
  return { date: `${year}-${month}-${day}`, hour }
}

/** Normalizes inclusive :59:59.999 boundaries to the next exact boundary. */
export const normalizeInclusiveRangeEnd = (date: Date): Date => {
  if (
    date.getUTCMinutes() === 59 &&
    date.getUTCSeconds() === 59 &&
    date.getUTCMilliseconds() === 999
  ) {
    return new Date(date.getTime() + 1)
  }
  return date
}

/** ISO-string variant of normalizeInclusiveRangeEnd with safe parsing. */
export const normalizeInclusiveRangeEndIso = (value?: string | null): string | undefined => {
  const parsedDate = parseIsoToDate(value)
  if (!parsedDate) return undefined
  return normalizeInclusiveRangeEnd(parsedDate).toISOString()
}

const getTimeZoneOffsetMs = (date: Date, timeZone: string): number => {
  const formatter = new Intl.DateTimeFormat('en-US', {
    timeZone,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
    hourCycle: 'h23',
  })
  const parts = formatter.formatToParts(date)
  const getPart = (type: string) => parts.find((part) => part.type === type)?.value
  const year = Number.parseInt(getPart('year') ?? '0', 10)
  const month = Number.parseInt(getPart('month') ?? '1', 10)
  const day = Number.parseInt(getPart('day') ?? '1', 10)
  const hour = Number.parseInt(getPart('hour') ?? '0', 10)
  const minute = Number.parseInt(getPart('minute') ?? '0', 10)
  const second = Number.parseInt(getPart('second') ?? '0', 10)
  const asUtcTimestamp = Date.UTC(year, month - 1, day, hour, minute, second, 0)
  return asUtcTimestamp - date.getTime()
}

const zonedDateTimeToUtc = (
  year: number,
  month: number,
  day: number,
  timeParts: UtcTimeParts,
  timeZone: string
): Date => {
  const baseTimestamp = Date.UTC(
    year,
    month - 1,
    day,
    timeParts.hours,
    timeParts.minutes,
    timeParts.seconds,
    timeParts.milliseconds
  )
  let utcTimestamp = baseTimestamp
  for (let attempt = 0; attempt < 2; attempt += 1) {
    const offset = getTimeZoneOffsetMs(new Date(utcTimestamp), timeZone)
    utcTimestamp = baseTimestamp - offset
  }
  return new Date(utcTimestamp)
}

export const parseDateOnlyToUtcDate = (
  date: string,
  options: UtcTimeParts
) => {
  if (!DATE_ONLY_PATTERN.test(date)) return undefined

  const [year, month, day] = date.split('-').map(Number)
  const utcDate = new Date(
    Date.UTC(
      year,
      month - 1,
      day,
      options.hours,
      options.minutes,
      options.seconds,
      options.milliseconds
    )
  )
  const isValidDate =
    Number.isFinite(utcDate.getTime()) &&
    utcDate.getUTCFullYear() === year &&
    utcDate.getUTCMonth() === month - 1 &&
    utcDate.getUTCDate() === day

  return isValidDate ? utcDate : undefined
}

/**
 * Parses graph-provided date strings while preserving their calendar-day intent.
 * Date-only values are anchored to UTC midnight to match dashboard API buckets.
 */
export const parseGraphDate = (value: string): Date => {
  const trimmedValue = value.trim()
  const dateOnly = parseDateOnlyToUtcDate(trimmedValue, START_OF_DAY_UTC)
  if (dateOnly) return dateOnly
  return new Date(trimmedValue)
}

const getUtcHourRange = (date: Date, hour: number, durationHours: number) => {
  const safeHour = Math.max(0, Math.min(23, hour))
  const start = new Date(date)
  start.setUTCHours(safeHour, 0, 0, 0)
  const end = new Date(start)
  end.setUTCHours(safeHour + durationHours - 1, 59, 59, 999)
  return { start, end }
}

/**
 * Builds an inclusive [start, end] range from a date + hour anchor.
 * The range spans durationHours and ends at xx:59:59.999.
 */
export const getRangeFromDateAndHour = (
  date: string,
  hour: number,
  durationHours: number,
  timezone?: string
) => {
  const safeHour = Math.max(0, Math.min(23, hour))
  const safeDuration = Math.max(1, durationHours)
  if (!DATE_ONLY_PATTERN.test(date)) {
    return getUtcHourRange(parseGraphDate(date), safeHour, safeDuration)
  }
  const [year, month, day] = date.split('-').map(Number)
  const zone = timezone ?? FALLBACK_TIMEZONE
  const start = zonedDateTimeToUtc(
    year,
    month,
    day,
    { ...START_OF_DAY_UTC, hours: safeHour },
    zone
  )
  const end = new Date(
    zonedDateTimeToUtc(
      year,
      month,
      day,
      { ...START_OF_DAY_UTC, hours: safeHour + safeDuration },
      zone
    ).getTime() - 1
  )
  return { start, end }
}

/**
 * Maps a Date to its 6-hour quarter-day segment index (0..3).
 */
export const getQuarterDaySegment = (date: Date) => Math.floor(date.getUTCHours() / 6)

/**
 * Returns the inclusive bucket range for a timestamp at the requested granularity.
 * This is the canonical boundary source used by graph rendering and drilldown filters.
 */
export const getBucketRangeForGranularity = (
  timestamp: string,
  granularity: DashboardTimeGranularity
) => {
  const date = new Date(timestamp)

  if (granularity === 'year') {
    const year = date.getUTCFullYear()
    return {
      from: new Date(Date.UTC(year, 0, 1, 0, 0, 0, 0)),
      to: new Date(Date.UTC(year, 11, 31, 23, 59, 59, 999)),
    }
  }

  if (granularity === 'quarter') {
    const year = date.getUTCFullYear()
    const startMonth = Math.floor(date.getUTCMonth() / 3) * 3
    return {
      from: new Date(Date.UTC(year, startMonth, 1, 0, 0, 0, 0)),
      to: new Date(Date.UTC(year, startMonth + 3, 0, 23, 59, 59, 999)),
    }
  }

  if (granularity === 'month') {
    const year = date.getUTCFullYear()
    const month = date.getUTCMonth()
    return {
      from: new Date(Date.UTC(year, month, 1, 0, 0, 0, 0)),
      to: new Date(Date.UTC(year, month + 1, 0, 23, 59, 59, 999)),
    }
  }

  if (granularity === 'week') {
    const start = new Date(date)
    const diff = start.getUTCDay() === 0 ? -6 : 1 - start.getUTCDay()
    start.setUTCDate(start.getUTCDate() + diff)
    start.setUTCHours(0, 0, 0, 0)
    const end = new Date(start)
    end.setUTCDate(end.getUTCDate() + 6)
    end.setUTCHours(23, 59, 59, 999)
    return { from: start, to: end }
  }

  if (granularity === 'day') {
    const year = date.getUTCFullYear()
    const month = date.getUTCMonth()
    const day = date.getUTCDate()
    return {
      from: new Date(Date.UTC(year, month, day, 0, 0, 0, 0)),
      to: new Date(Date.UTC(year, month, day, 23, 59, 59, 999)),
    }
  }

  if (granularity === 'quarter_day') {
    const quarterStartHour = Math.floor(date.getUTCHours() / 6) * 6
    const { start, end } = getUtcHourRange(date, quarterStartHour, 6)
    return { from: start, to: end }
  }

  const { start, end } = getUtcHourRange(date, date.getUTCHours(), 1)
  return { from: start, to: end }
}

/**
 * Converts a YYYY-MM-DD date to UTC start-of-day ISO timestamp.
 */
export const toIsoStartOfDay = (date: string, timezone?: string) =>
  toIsoDateBoundary(date, START_OF_DAY_UTC, timezone)

/**
 * Converts a YYYY-MM-DD date to UTC end-of-day ISO timestamp.
 */
export const toIsoEndOfDay = (date: string, timezone?: string) =>
  toIsoDateBoundary(date, END_OF_DAY_UTC, timezone)

/**
 * Converts optional date-only boundaries into API-friendly ISO range values.
 */
export const toIsoRangeFromDateOnly = (fromDate?: string, toDate?: string, timezone?: string) => ({
  fromDate: fromDate ? toIsoDateBoundary(fromDate, START_OF_DAY_UTC, timezone) ?? undefined : undefined,
  toDate: toDate ? toIsoDateBoundary(toDate, END_OF_DAY_UTC, timezone) ?? undefined : undefined,
})

const toIsoDateBoundary = (
  date: string,
  timeParts: UtcTimeParts,
  timezone: string = FALLBACK_TIMEZONE
) => {
  if (!DATE_ONLY_PATTERN.test(date)) return undefined
  const [year, month, day] = date.split('-').map(Number)
  const utcFallback = parseDateOnlyToUtcDate(date, timeParts)
  if (!utcFallback) return undefined

  if (timezone === FALLBACK_TIMEZONE) return utcFallback.toISOString()

  try {
    const isEndOfDayBoundary =
      timeParts.hours === END_OF_DAY_UTC.hours &&
      timeParts.minutes === END_OF_DAY_UTC.minutes &&
      timeParts.seconds === END_OF_DAY_UTC.seconds &&
      timeParts.milliseconds === END_OF_DAY_UTC.milliseconds
    if (isEndOfDayBoundary) {
      const nextDayUtc = new Date(Date.UTC(year, month - 1, day + 1, 0, 0, 0, 0))
      const nextDayStart = zonedDateTimeToUtc(
        nextDayUtc.getUTCFullYear(),
        nextDayUtc.getUTCMonth() + 1,
        nextDayUtc.getUTCDate(),
        START_OF_DAY_UTC,
        timezone
      )
      return new Date(nextDayStart.getTime() - 1).toISOString()
    }

    return zonedDateTimeToUtc(year, month, day, timeParts, timezone).toISOString()
  } catch {
    return utcFallback.toISOString()
  }
}
